// Bring your numbers (port-in) and the port-out PIN (docs/specs/telephony-compliance.md sections
// 2.2 to 2.8, stories TC9a and TC10). Mirrors crates/server/src/ports/service.rs (port-in views
// and inputs) and crates/api-types/src/port_out.rs. Admins write; managers read the port list
// and each port. Port-out routes are Admins only. Exported as the `portsAdmin` namespace.
//
// The bill upload (multipart) and the LOA PDF (a binary with its SHA-256 in a header) don't fit
// the JSON transport; each app sends those with its own fetch, at `portBillPath` and
// `portLoaPath`.

import type { ApiTransport } from "./client.ts";
import type { Timestamp, Uuid } from "./types.ts";

/** `YYYY-MM-DD`. */
export type IsoDate = string;

/** Where a port stands (spec 2.4). */
export type PortState =
  | "draft"
  | "signed"
  | "submitting"
  | "submitted"
  | "needs_info"
  | "foc_confirmed"
  | "porting"
  | "ported"
  | "cancelled"
  | "needs_attention";

/** `full`: every number on the account. `partial`: the rest stay with the old carrier. */
export type PortKind = "full" | "partial";

export type PortNumberType = "local" | "toll_free";

/** One number of a check that can move. */
export interface CheckedNumber {
  e164: string;
  fast_port: boolean;
  number_type: PortNumberType;
}

/** The numbers of one current carrier: one port. */
export interface CheckGroup {
  carrier: string;
  numbers: CheckedNumber[];
}

/** Why a number was left out. */
export type ExcludedReason = "already_on_kite" | "not_portable" | "no_carrier" | "duplicate";

export interface ExcludedNumber {
  e164: string;
  reason: ExcludedReason;
  /** The carrier's reason, scrubbed. */
  detail: string | null;
}

/** `POST /v1/admin/ports/check`. */
export interface CheckView {
  groups: CheckGroup[];
  excluded: ExcludedNumber[];
}

export interface PortAddress {
  street: string;
  unit: string | null;
  city: string;
  state: string | null;
  zip: string | null;
}

/** One number of a port and where it lands. */
export interface PortNumber {
  e164: string;
  portable: boolean;
  fast_port: boolean;
  number_type: PortNumberType;
  brand_id: Uuid | null;
  owner_user_id: Uuid | null;
  label: string;
  texting: boolean;
  emergency_address_id: Uuid | null;
}

/** A carrier comment as the customer reads it (scrubbed). */
export interface PortComment {
  at: Timestamp | null;
  text: string;
}

/** A port. The PIN never comes back; the account number shows its last four characters. */
export interface Port {
  id: Uuid;
  state: PortState;
  kind: PortKind;
  partial_remaining: string | null;
  /** The customer's current carrier, by name. */
  losing_carrier: string;
  account_holder: string;
  authorized_name: string;
  authorized_title: string;
  billing_number: string | null;
  service_address: PortAddress;
  account_number_last4: string | null;
  pin_set: boolean;
  requested_foc_date: IsoDate | null;
  /** The earliest date the transfer can ask for, today. */
  earliest_foc_date: IsoDate;
  foc_at: Timestamp | null;
  version: number;
  bill_uploaded: boolean;
  /** The current version is signed. */
  signed: boolean;
  /** Latest first. */
  carrier_comments: PortComment[];
  numbers: PortNumber[];
  created_at: Timestamp;
  submitted_at: Timestamp | null;
  cancelled_at: Timestamp | null;
}

/** `GET /v1/admin/ports` and `POST /v1/admin/ports`: newest first. */
export interface PortList {
  ports: Port[];
}

export interface PortAddressInput {
  street: string;
  unit?: string | null;
  city: string;
  state: string;
  zip: string;
}

/** Where one number lands. */
export interface NumberLanding {
  e164: string;
  brand_id?: Uuid | null;
  owner_user_id?: Uuid | null;
  label?: string | null;
  texting?: boolean | null;
  emergency_address_id?: Uuid | null;
}

/** `PUT /v1/admin/ports/{id}`: the fields to change; anything left out stays. An empty `pin`
 * removes the PIN. */
export interface PortEdit {
  kind?: PortKind;
  partial_remaining?: string | null;
  account_holder?: string;
  authorized_name?: string;
  authorized_title?: string;
  billing_number?: string | null;
  service_address?: PortAddressInput;
  account_number?: string;
  pin?: string;
  requested_foc_date?: IsoDate | null;
  numbers?: NumberLanding[];
}

/** The version of the consent text the sign dialog shows (crates/server/src/ports/loa.rs). */
export const PORT_CONSENT_TEXT_VERSION = "port-loa-consent-2026-11-01";

/** "I am authorized on this account and I agree to sign electronically." */
export const PORT_CONSENT_TEXT =
  "I am authorized on this account and I agree to sign electronically.";

/** `POST /v1/admin/ports/{id}/sign`. */
export interface SignPort {
  consent: boolean;
  consent_text_version: string;
  typed_name: string;
  title: string;
  /** The SHA-256 of the LOA read (the `x-document-sha256` header of `GET .../loa`). */
  document_sha256?: string | null;
}

// Port-out ----------------------------------------------------------------------------------

/** `GET /v1/admin/port-out`. */
export interface PortOutSettings {
  /** `KV` and 10 characters, stable for the company. */
  account_number: string;
  pin_set: boolean;
  pin_set_at: Timestamp | null;
}

/** `POST /v1/admin/port-out/reveal`. */
export interface PortOutPinReveal {
  pin: string;
}

export type PortOutStatus =
  | "pending"
  | "authorized"
  | "rejected"
  | "rejected_pending"
  | "foc_confirmed"
  | "completed"
  | "cancelled";

export interface PortOutRequest {
  id: Uuid;
  e164: string;
  numbers: string[];
  phone_number_id: Uuid | null;
  status: PortOutStatus;
  requested_at: Timestamp;
  foc_at: Timestamp | null;
  completed_at: Timestamp | null;
  decision: string | null;
  decision_reason: string | null;
  decided_at: Timestamp | null;
}

/** `GET /v1/admin/port-outs`: newest first. */
export interface PortOutList {
  port_outs: PortOutRequest[];
}

const enc = encodeURIComponent;
const BASE = "/v1/admin/ports";

/** `POST`, multipart: the bill copy (PDF, PNG or JPEG, up to 10 MB). Answers the port. */
export const portBillPath = (id: Uuid) => `${BASE}/${enc(id)}/bill`;
/** `GET`: the unsigned LOA PDF; `x-document-sha256` carries its SHA-256. */
export const portLoaPath = (id: Uuid) => `${BASE}/${enc(id)}/loa`;

export class PortsAdminApi {
  constructor(private readonly client: ApiTransport) {}

  /** Up to 100 numbers, grouped by current carrier. Read only and free. */
  check(numbers: string[]) {
    return this.client.post<CheckView>(`${BASE}/check`, { numbers });
  }
  /** 201: one draft port per current carrier of these numbers. */
  create(numbers: string[]) {
    return this.client.post<PortList>(BASE, { numbers });
  }
  /** Admins and managers. */
  list(signal?: AbortSignal) {
    return this.client.get<PortList>(BASE, signal);
  }
  /** Admins and managers. */
  get(id: Uuid, signal?: AbortSignal) {
    return this.client.get<Port>(`${BASE}/${enc(id)}`, signal);
  }
  /** 409 `port_locked` unless draft, signed or needs a fix; a signed port goes back to draft. */
  edit(id: Uuid, body: PortEdit) {
    return this.client.put<Port>(`${BASE}/${enc(id)}`, body);
  }
  /** 409 `document_changed`; 422 `consent_required`, `invalid_loa`, `address_needed`,
   * `bill_needed`, `foc_too_soon`. */
  sign(id: Uuid, body: SignPort) {
    return this.client.post<Port>(`${BASE}/${enc(id)}/sign`, body);
  }
  /** 202, once per signed version (the server's dedupe key); 409 `not_signed`. */
  submit(id: Uuid) {
    return this.client.post<Port>(`${BASE}/${enc(id)}/submit`);
  }
  /** 202, before the switch. */
  cancel(id: Uuid) {
    return this.client.post<Port>(`${BASE}/${enc(id)}/cancel`);
  }

  portOut(signal?: AbortSignal) {
    return this.client.get<PortOutSettings>("/v1/admin/port-out", signal);
  }
  /** 6 to 10 digits; 422 `bad_pin`. */
  setPortOutPin(pin: string) {
    return this.client.put<PortOutSettings>("/v1/admin/port-out", { pin });
  }
  /** 403 `reauth_required` without a sign-in in the last 10 minutes; 404 `no_pin`. */
  revealPortOutPin() {
    return this.client.post<PortOutPinReveal>("/v1/admin/port-out/reveal");
  }
  portOuts(signal?: AbortSignal) {
    return this.client.get<PortOutList>("/v1/admin/port-outs", signal);
  }
}
